import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Canvas from "../canvas";
import { useBoardFile } from "../../context/boardFileContext";
import { useCanvas } from "../../context/canvasContext";
import { v1 as uuid } from "uuid";
import { debounce } from 'lodash';

import {
  addObj,
  addPath,
  addText,
  emitCanvasFile,
  emitDrawingPath,
  emitModify,
  modifyObj,
  removeObject,
  addImage,
  emitClonedObj,
  addClonedObject,
  addToGroup,
  emitRoomID,
  emitCanvasData,
  onBackgroundColorChange,
  clearCanvasListen,
  onShapeColorChange,
  ListenUndo,
  ListenRedo,
  reloadStack,
  listenSendToBack,
  listenBringToFront,
  onStrokeColorChange,
  listenUngroup,
  listenUpdateAll
} from "../../socket";
import axios from "axios";
import { boardServices, workwiseServices } from "../../api";
import { useDocument } from "../../context/documentInfoContex";
import { centerAllObjectsInViewport, formatFileSize, isOffline, removeOfflineData, resolveDataConflict, saveToLocalStorage } from "../../utils/helper";
import WaterMark from "../WaterMark";
import Loader from "../SkeletonLoader/skeletonLoader";



const CanvasWrapper = ({ contentLoading, setContentLoading }) => {
  const { saveBoardData, loadBoardFile, boardFileName } = useBoardFile();
  const [update, setUpdate] = useState(null)
  const {
    canvasRef,
    prepareCanvas,
    createGrid,
    Panning,
    createImage,
    getActiveObject,
    saveStateToUndoStack,
    undoStack,
    redoStack,
    reloadStack,
    saveStateToReloadStack,
    savingLastChanges,
    lastChange

  } = useCanvas();

  const {
    setSavingData,
    isReadOnly,
    setIsExternalAllow,
    userInfo,
    documentInfo,
    setIsConflictModalVisible,
    setServerData,
    isConflictModalVisible,
    isLoadingVersion,
    conflictPreviewVerion,
    boardData,
    setBoardData,
  } = useDocument();

  let { id } = useParams();
  const [movementData, setMovementData] = useState();
  const url_string = window.location.href; //window.location.href
  const url = new URL(url_string);
  const mileboardName = url.searchParams.get("mileboardname");

  //Send the id to the server.
  //Checks if the board exists, if not. It creates the board.
  //If it does, it loads the while.
  React.useEffect(() => {
    //emitCanvasFile(id);
    !isOffline() && emitRoomID(id);
  }, [id]);

  const baseURL = 'https://board.workw.com/api/';
  // const baseURL = "http://localhost:3000/api/";

  const hasInitializedRef = React.useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      prepareCanvas(id);
      hasInitializedRef.current = true;
    }

    const loadData = async () => {
      if (!canvasRef.current) return;
      setContentLoading(true);


      function loadDataIntoCanvas(data) {
        
        if (!canvasRef?.current || !data) return;
        
        canvasRef.current.loadFromJSON(data, () => {
          canvasRef.current.renderAll();
          centerAllObjectsInViewport(canvasRef.current);
          saveStateToReloadStack();
          setContentLoading(false);
        });
      }
      
      try {
        const offlineBoard = JSON.parse(localStorage.getItem("offlineIdeationData") || "{}");
        const localData = offlineBoard[id]?.data || null;
        const localDate = offlineBoard[id]?.lastUpdateTime ? new Date(offlineBoard[id].lastUpdateTime) : null;

        const serverBoardData = await boardServices.getBoardData(id);
        const serverCanvasData = serverBoardData?.mileboardData?.[0] || null;
        const serverDate = serverBoardData?.updatedAt ? new Date(serverBoardData.updatedAt) : null;
        setServerData(serverBoardData);

        let finalData = null;

        // 1. Preview mode chosen from conflict modal
        if (conflictPreviewVerion) {

          finalData = conflictPreviewVerion === "offline" ? localData : serverCanvasData;
          loadDataIntoCanvas(finalData);
          return;
        }

        // 2. Conflict: both exist, server newer
        if (localData && serverCanvasData && serverDate && localDate && serverDate > localDate) {
          setIsConflictModalVisible(true);
          setContentLoading(false);
          return;
        }

        // 3. Local is newer
        if (localData && (!serverCanvasData || (serverDate && localDate && localDate > serverDate))) {
          finalData = localData;
          loadDataIntoCanvas(localData);
          await boardServices.updateBoardData(id, localData, setSavingData);
          removeOfflineData(id);
          return;
        }

        // 4. Only server available
        if (serverCanvasData) {
          finalData = serverCanvasData;
          loadDataIntoCanvas(serverCanvasData);
          return;
        }

        // 5. No data at all
        setContentLoading(false);

        // Update metadata if we had data
        if (finalData) {
          setIsExternalAllow(finalData?.isExternal || false);
          setSavingData({
            isSaving: "",
            lastSavedDateTime: "",
            fileSize: formatFileSize(new TextEncoder().encode(JSON.stringify(finalData)).length),
          });
        }

      } catch (error) {
        console.error(`Error loading board ${id}:`, error);
        setContentLoading(false);
      }

    };

    loadData();

    return () => {
      // Cleanup if needed
    };
  }, [id, conflictPreviewVerion]);

  React.useEffect(() => {
    //loadBoardFile(id, canvasRef);
    const createBoard = async (id, canvasRef) => {
      try {
        const response = await axios.post(
          `${baseURL}boards/create`,
          { id }
        );

      } catch (error) {
        console.log(error);
      }
    };
    createBoard(id, canvasRef);
  }, [id, canvasRef]);

  React.useEffect(() => {
    window.addEventListener("dragover", (event) => {
      event.stopPropagation();
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    });
    if (isReadOnly) {
      window.addEventListener("drop", (event) => {
        event.stopPropagation();
        event.preventDefault();
        const fileList = event.dataTransfer.files;
        for (const file of fileList) {
          if (file.type && !file.type.startsWith("image/")) {
            // console.log("File is not an image.", file.type, file);
            return;
          }
          const reader = new FileReader();
          reader.addEventListener("load", (event) => {
            let imgDataBase64 = event.target.result;
            createImage(imgDataBase64, id);
          });
          reader.readAsDataURL(file);
        }
      });
    }

    // window.addEventListener("copy", copyItem);
    // let _clipboard;
    // // async function pasteItem(e) {
    // //   if (canvasRef.current.getActiveObject()) {
    // //     _clipboard.clone(function (clonedObj) {
    // //       canvasRef.current.discardActiveObject();
    // //       clonedObj.set({
    // //         left: clonedObj.left + 10,
    // //         top: clonedObj.top + 10,
    // //         evented: true,
    // //       });
    // //       if (clonedObj.type === "activeSelection") {
    // //         // active selection needs a reference to the canvas.
    // //         clonedObj.canvas = canvasRef.current;
    // //         clonedObj.forEachObject(function (obj) {
    // //           console.log("I occurred")
    // //           console.log(canvasRef.current.getObjects().indexOf(canvasRef.current.setActiveObject(obj)))
    // //           canvasRef.current.add(obj);
    // //           //emitClonedObj(obj);
    // //           clonedObj.setCoords();
    // //         });
    // //         // this should solve the unselectability

    // //       } else {
    // //         canvasRef.current.add(clonedObj);
    // //         emitClonedObj(clonedObj);
    // //         clonedObj.setCoords();
    // //    }
    // //       _clipboard.top += 10;
    // //       _clipboard.left += 10;
    // //       canvasRef.current.setActiveObject(clonedObj);
    // //       canvasRef.current.requestRenderAll();
    // //     });
    //   }

    const pasteItem = async () => {
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            const blob = await clipboardItem.getType(type);
            let imgURL = URL.createObjectURL(blob);
            createImage(imgURL);
          }
        }
      } catch (err) {
        console.error(err.name, err.message);
      }
    }
    window.addEventListener("paste", pasteItem);
  }, [canvasRef, createImage]);

  React.useEffect(() => {
    Panning(id);
  }, [Panning]);

  //React.useEffect(() => {
  //  createGrid();
  //}, [createGrid]);



  //resize canvas according to screensize.
  // React.useEffect(() => {
  //   const resizeCanvas = () => {
  //     canvasRef.current.setDimensions({
  //       width: width,
  //       height: height,
  //     });
  //     canvasRef.current.renderAll();
  //   };
  //   window.addEventListener("resize", resizeCanvas);
  //   return () => window.removeEventListener("resize", resizeCanvas);
  // }, [canvasRef, width, height]);

  //event listeners for canvas
  React.useEffect(() => {

    if (isLoadingVersion.current) return;

    if (canvasRef && !isConflictModalVisible && !conflictPreviewVerion) {
      //Executed when an object is modified, i.e the dimensions,color etc.
      canvasRef.current.on("object:modified", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being moved
            textBox.exitEditing();
          }
          const modifiedObj = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(canvasRef.current.toJSON(modifiedObj));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          !isOffline() && emitModify(id, modifiedObj);
          //saveBoardData(id, canvasRef);
        }
      });
      //Executed when text is changed.
      canvasRef.current.on("text:changed", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          const modifiedTxt = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(modifiedTxt);
          // console.log(canvasRef.current.toJSON(modifiedTxt));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          !isOffline() && emitModify(id, modifiedTxt);
          saveBoardData(id, canvasRef);
        }
      });
      //Executed when the object is moving.
      canvasRef.current.on("object:moving", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being moved
            textBox.exitEditing();
          }
          const modifiedObj = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(canvasRef.current.toJSON(modifiedObj));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          setMovementData(modifiedObj);
          // emitModify(modifiedObj);
          saveBoardData(id, canvasRef);
        }
      });
      canvasRef.current.on("path:created", function (e) {

        if (isLoadingVersion.current) return;

        let path = e.path;
        path.set({ id: uuid() });
        path.set({ type: "path" });
        // console.log(canvasRef.current.toJSON({path}));
        setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
        saveStateToUndoStack()                  //Path created with pen tool saves in UndoStack here                     //Implemented to make Undo Redo work for Pen tool
        !isOffline() && emitDrawingPath({ room: id, path, id: path.id });
        saveBoardData(id, canvasRef);
      });
      canvasRef.current.on("object:removed", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being removed
            textBox.exitEditing();
          }
        }
        setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
      });

      // canvasRef, saveBoardData, boardFileName, getActiveObject, id, isConflictModalVisible, isLoadingVersion

      modifyObj(canvasRef, saveStateToUndoStack, setBoardData);
      addObj(canvasRef, saveStateToUndoStack, setBoardData);
      addText(canvasRef, saveStateToUndoStack, setBoardData);
      addPath(canvasRef, saveStateToUndoStack, setBoardData);
      removeObject(canvasRef, saveStateToUndoStack, setBoardData);
      addImage(canvasRef, saveStateToUndoStack, setBoardData);
      addClonedObject(canvasRef, saveStateToUndoStack, setBoardData);
      addToGroup(canvasRef, saveStateToUndoStack, setBoardData);
      //Below Function is Socket Listener Function which listens to background changes and reflects in connected clients,setBoardData C
      onBackgroundColorChange(canvasRef, saveStateToUndoStack, setBoardData)
      //Function below Listen to Server when clear-canvas is emitt,setBoardDataed
      clearCanvasListen(canvasRef, saveStateToUndoStack, setBoardData);
      onShapeColorChange(canvasRef, saveStateToUndoStack, setBoardData);
      //Listener to listen to Undo Emit from Serv,setBoardDataer
      ListenUndo(canvasRef, undoStack, redoStack, saveStateToUndoStack, setBoardData);
      //Listener to listen to Redo Emit from Serv,setBoardDataer
      ListenRedo(canvasRef, undoStack, redoStack, saveStateToUndoStack, setBoardData);
      !isOffline() && listenSendToBack(canvasRef, saveStateToUndoStack, setBoardData);
      isOffline() && listenBringToFront(canvasRef, saveStateToUndoStack, setBoardData);
      onStrokeColorChange(canvasRef, saveStateToUndoStack, setBoardData)
      !isOffline() && listenUngroup(canvasRef, saveStateToUndoStack, setBoardData);
      listenUpdateAll(id, canvasRef, saveStateToUndoStack, setBoardData);
    }
  }, [canvasRef, saveBoardData, boardFileName, getActiveObject, id, isConflictModalVisible, isLoadingVersion]);

  React.useEffect(() => {

    if (boardData && boardData.length !== 0) {
      const updateDataOnServer = async (data) => {
        const isOffline = !window.navigator.onLine;
        if (isOffline) {
          // Save to localStorage if offline
          saveToLocalStorage(id, data);
          return;
        }
        await boardServices.updateBoardData(id, data, setSavingData);
        // Clear localStorage after successful sync
        removeOfflineData(id);
        workwiseServices.saveDocumentlastUpdate(id, setSavingData, data);
      };

      let timer = setTimeout(async () => {
        updateDataOnServer(boardData);
      }, 100);

      return () => clearTimeout(timer);

    }
  }, [boardData, undoStack, redoStack]);

  React.useEffect(() => {
    let timer = setTimeout(() => {
     !isOffline() && emitModify(movementData);
      //  console.log("timer function called");
    }, 500)

    return () => clearTimeout(timer);

  }, [movementData])


  return (
    <>
      {contentLoading && <Loader />}
      <div style={{
        pointerEvents: isReadOnly ? 'none' : 'auto',
        visibility: contentLoading ? 'hidden' : 'visible'
      }} id='my-canvas-board'>
        {isReadOnly && userInfo?.userId !== documentInfo?.creator?.id && !conflictPreviewVerion && <WaterMark />}
        <Canvas />
      </div>
    </>
  );
};

export default CanvasWrapper;
